import React, { useEffect } from "react";
import {
  Button,
  Modal,
  Box,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3";
import { MetricsState } from "../../logging";
import { useHttpRequest, LoadingState } from "../../http";
import { DisplayBanner } from "../../banner/bannerSlice";
import { UserAccess } from "../UserAccess";
import styles from "../permissions_page.css";
import { changesAfterFirstFailureBanner } from "../../common/utils/ErrorBannerMessages";

export interface RemoveUserResult {
  user: UserAccess;
  teamId: string;
}

export interface RemoveUserModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  item: Partial<RemoveUserResult>;
  metrics: MetricsState;
  reloadPermissionsTable: () => void;
  setBanner: (banner: DisplayBanner | undefined) => void;
}

const getSuccessfulBannerMessage = (
  admin: UserAccess,
  statusCode: number
): string => {
  const pendingResultsMessage =
    statusCode === 200
      ? ""
      : "  This change may take a while to appear in your team.";
  return `${admin.userFullName} has been removed from ${admin.teamName} admins.${pendingResultsMessage}`;
};

const RemoveUserModal: React.FC<RemoveUserModalProps> = ({
  showModal,
  setShowModal,
  item,
  metrics,
  reloadPermissionsTable,
  setBanner,
}): JSX.Element => {
  const admin = item.user as UserAccess;
  const {
    loadingState: removeUserLoadingState,
    sendRequest: removeUserSendRequest,
    error: removeUserError,
    response: removeUserAxiosResponse,
  } = useHttpRequest("removeTeamAdmins", metrics);

  useEffect(() => {
    if (
      removeUserLoadingState === LoadingState.RESOLVED &&
      removeUserAxiosResponse
    ) {
      setBanner({
        type: "success",
        content: getSuccessfulBannerMessage(
          admin,
          removeUserAxiosResponse.status
        ),
        dissmissible: true,
      });
      if (removeUserAxiosResponse.status === 200) {
        reloadPermissionsTable();
      }
      setShowModal(false);
    }
    if (removeUserLoadingState === LoadingState.REJECTED || removeUserError) {
      // TODO: handle 404 error differently
      setBanner(changesAfterFirstFailureBanner);
      setShowModal(false);
    }
  }, [
    removeUserLoadingState,
    removeUserAxiosResponse,
    removeUserError,
    setShowModal,
    setBanner,
  ]);

  return (
    <div className={styles["centered-modal"]}>
      <Modal
        data-test-id="remove-user-modal"
        header={`Remove ${admin.userFullName}`}
        visible={showModal}
        closeAriaLabel="exit modal dismiss button"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-test-id="remove-user-cancel-btn"
                variant="link"
                formAction="none"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                formAction="none"
                loading={removeUserLoadingState === LoadingState.LOADING}
                onClick={() => {
                  removeUserSendRequest({
                    url: `/api/v1/teams/${item.teamId}/admins/remove`,
                    method: "POST",
                    data: {
                      teamId: item.teamId,
                      employeeIds: [admin.userEmployeeId],
                    },
                  });
                }}
              >
                Remove
              </Button>
            </SpaceBetween>
          </Box>
        }
        onDismiss={() => {
          setShowModal(false);
        }}
      >{`${admin.userFullName} will be removed as an admin of ${admin.teamName}.`}</Modal>
    </div>
  );
};

export default RemoveUserModal;
